import React, { useRef, useEffect, useState, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import classNames from '../lib/class-names';

const CompleteDialog = ({ isOpen, signingRedirectURL, onSubmit, onClose, documentsRequired = false }) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog onClose={() => !signingRedirectURL && onClose()} as="div" className="fixed inset-0 z-10 overflow-y-auto">
        <div className="min-h-screen px-4 text-center">
          <Dialog.Overlay className="fixed inset-0 bg-gray-800 opacity-50" />
          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
          <div className={classNames(
            "inline-block w-full p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl max-w-xl"
            )}
          >
          {
            signingRedirectURL //NOTE* signingRedirectURL is for activeWorkflow redirect (if passed request is now completed) 
            ? <Redirect redirectURL={signingRedirectURL} />
            : <Complete onSubmit={onSubmit} onClose={onClose} documentsRequired={documentsRequired} />
          }
          </div>
        </div>
      </Dialog>
    </Transition>
  )
};

const REDIRECT_TIMEOUT_SECONDS = 8;

function Redirect ({ redirectURL }) {
  var redirectRef = useRef(redirectURL);
  var [ timeRemaining, setTimeRemaining ] = useState(REDIRECT_TIMEOUT_SECONDS);
  
  useEffect(
    function autoRedirect () {
      var timerId;

      if (!timeRemaining) {
        window.location.href = redirectRef.current;
      } else {
        timerId = window.setTimeout(() => setTimeRemaining(s => s-1), 1000);
      }

      return () => clearTimeout(timerId);
    }, 
    [ timeRemaining ]
  );

  return (
    <>
      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Request Completed</Dialog.Title>
        <p className="text-sm text-gray-500">
         The Information Request was completed successfully.
        </p>
        <div className="border border-gray-200 rounded-md p-4 mt-2">
          <div className="flex items-start md:items-center space-x-3">
            <div style={{ minWidth: '2.25rem', minHeight: '2.25rem', backgroundColor: "#00c951"}} className="w-6 h-6 flex items-center justify-center rounded text-white">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" className="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
              </svg>

            </div>
            <span className="text-sm leading-tight font-medium text-gray-900 m-0">The documents are now ready for signing.</span>
          </div>
        </div>
  
      <div className="mt-2">
        <p className="text-sm text-gray-900 font-medium text-gray-800">Document Signing</p>
        <p className="text-sm text-gray-600">The agent has requested signing of the documents.</p>
         <p className="p-2 text-sm rounded border border-gray-200 text-medium my-2">You will be automatically redirected in {timeRemaining} seconds</p>
        <p className="text-xs text-gray-800 leading-tight mt-2">If you are not automatically redirected, please follow the link to <a href={redirectURL} rel="noopener nofollow" style={{ textDecoration: 'underline' }} className="text-blue-700">view the signing request</a></p>
      </div>
    </>
  )
}

function Complete ({ onClose, onSubmit, documentsRequired }) {
  return (
    <>
      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Complete Request</Dialog.Title>
      {
        documentsRequired &&
        <div className="border border-gray-200 rounded-md p-4 mt-2">
          <div className="flex items-start md:items-center space-x-3">
            <div style={{ minWidth: '2.25rem', minHeight: '2.25rem', backgroundColor: "#ffd230"}} className="w-6 h-6 flex items-center justify-center rounded text-white">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"  className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
            </div>
            <span className="text-sm leading-tight font-medium text-gray-900 m-0">Please ensure you have uploaded all documents requested by the agent before completion.</span>
          </div>
        </div>
      }
      <div className="mt-2">
        <p className="text-sm text-gray-500">
         Are you sure you want to complete the form? You will not be able to make any changes after completion.
        </p>
      </div>

      <div className="flex justify-end mt-4">
        <button
          type="button"
          className="mr-2 inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-transparent rounded-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
          onClick={onSubmit}
        >
          Yes, Complete
        </button>
      </div>
    </>
  )
}

export default CompleteDialog;
